import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Printer, PaperPlaneTilt, CheckCircle, Warning } from '@phosphor-icons/react';
import TI from '../../components/brand/TI';
import SummaryCards from '../../components/hale-review/SummaryCards';
import AuditTimeline from '../../components/hale-review/AuditTimeline';
import DocumentChecklist from '../../components/hale-review/DocumentChecklist';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IVORY = '#F5F1E8';
const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const HALE_RED = '#E63946';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';
const ROUTING_EMAIL = 'regulatory@tagc.us';

/* /admin/toxintel/samples — ToxIntel Generic Workflow Output Library +
 * regulatory@tagc.us routing status. Admin-only.
 *
 * Reuses HaleAuditSamplesPage primitives (SummaryCards, AuditTimeline,
 * DocumentChecklist, SAMPLE watermark) so we don't duplicate components.
 * Synthetic data only — "Sample Brand Co." / "Generic Product A". */

const SAMPLE = {
  brand: 'Sample Brand Co.',
  product: 'Generic Product A',
  contact: 'alex@samplebrand.example',
  category: 'Body care · leave-on',
  submitted_at: '2026-02-10T15:04:00Z',
  routing_id: 'TI-26-0001-SAMPLE',
};

const CHECKLIST = [
  { id: 'formula',   label: 'Ingredient list / formula',           status: 'complete' },
  { id: 'sds',       label: 'SDS',                                 status: 'missing' },
  { id: 'coa',       label: 'COA',                                 status: 'missing' },
  { id: 'ifra',      label: 'IFRA certificate (fragrance)',        status: 'not_applicable' },
  { id: 'allergen',  label: 'Allergen statement (fragrance)',      status: 'not_applicable' },
  { id: 'packaging', label: 'Packaging specifications',            status: 'complete' },
  { id: 'claims',    label: 'Claims copy',                         status: 'complete' },
  { id: 'use',       label: 'Product use instructions',            status: 'complete' },
  { id: 'tests',     label: 'Test data / lab reports',             status: 'missing' },
  { id: 'retailer',  label: 'Retailer requirements',               status: 'not_applicable' },
];

const SAMPLES = [
  { id: '01', title: 'Assessment Request Confirmation',         audience: 'Brand',                   format: 'Dashboard', visible: true },
  { id: '02', title: 'Admin Submission Alert',                  audience: 'AG · Internal',           format: 'Email',     visible: false },
  { id: '03', title: 'ToxIntel Admin Intake Record',            audience: 'ToxIntel Admin',          format: 'Record',    visible: false },
  { id: '04', title: 'Document Checklist Output',               audience: 'ToxIntel Admin · Reviewer', format: 'Record',  visible: false },
  { id: '05', title: 'Reviewer Intake Summary',                 audience: 'AG · Reviewer',           format: 'Record',    visible: false },
  { id: '06', title: 'Additional Information Request',          audience: 'Brand',                   format: 'Email',     visible: true },
  { id: '07', title: 'TRA / QRA Assessment Sample',             audience: 'Internal + Released',     format: 'Record',    visible: 'partial' },
  { id: '08', title: 'Prop 65 Exposure Assessment Sample',      audience: 'Internal + Released',     format: 'Record',    visible: 'partial' },
  { id: '09', title: 'Regulatory Readiness Review Sample',      audience: 'Brand',                   format: 'Record',    visible: true },
  { id: '10', title: 'Claims Substantiation Review Sample',     audience: 'Brand',                   format: 'Record',    visible: true },
  { id: '11', title: 'Packaging / RSL Intelligence Sample',     audience: 'Brand · Internal',        format: 'Record',    visible: 'partial' },
  { id: '12', title: 'Scientific Output Complete',              audience: 'ToxIntel Admin · Brand · Hale', format: 'Record', visible: 'partial' },
  { id: '13', title: 'Internal Reviewer Notes Sample',          audience: 'Internal Only',           format: 'Record',    visible: false },
  { id: '14', title: 'Released Findings Sample',                audience: 'Brand · Hale',            format: 'Record',    visible: true },
  { id: '15', title: 'ToxIntel Dashboard Status Update',        audience: 'Brand',                   format: 'Dashboard', visible: true },
  { id: '16', title: 'Reviewer Queue Timeline',                 audience: 'ToxIntel Admin · Reviewer', format: 'Timeline',visible: false },
  { id: '17', title: 'Email Template Library',                  audience: 'Mixed',                   format: 'Email',     visible: 'partial' },
  { id: '18', title: 'PDF / Report Sample',                     audience: 'ToxIntel Admin',          format: 'PDF',       visible: false },
  { id: '19', title: 'Export Inventory',                        audience: 'ToxIntel Admin',          format: 'Record',    visible: false },
];

function Watermark() {
  return (
    <div className="text-[10px] font-mono uppercase tracking-[0.25em] px-3 py-1.5 inline-block rounded-sm mb-3"
         style={{ backgroundColor: 'rgba(230,57,70,0.10)', color: HALE_RED, border: `1px solid ${HALE_RED}` }}
         data-testid="toxintel-sample-watermark">
      SAMPLE · Generic ToxIntel Output · Not a real assessment
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b last:border-0" style={{ borderColor: RULE }}>
      <span className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: MUTED }}>{k}</span>
      <span className="text-[13px] font-semibold text-right" style={{ color: SLATE }}>{v}</span>
    </div>
  );
}

function Section({ id, title, audience, format, children }) {
  return (
    <section id={id} className="rounded-sm border p-6 mb-6" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }} data-testid={`toxintel-sample-${id}`}>
      <div className="mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>{id} · {audience} · {format}</p>
        <h3 className="text-xl font-extrabold tracking-tight mt-1" style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>{title}</h3>
      </div>
      <Watermark />
      {children}
    </section>
  );
}

function SendSampleButton({ templateId, toEmail, label = 'Send to me', testid }) {
  const [state, setState] = useState('idle');
  const [feedback, setFeedback] = useState(null);
  const onClick = async () => {
    setState('sending'); setFeedback(null);
    try {
      const r = await axios.post(`${API_URL}/api/hale-review/samples/send-email`,
        { template_id: templateId, to_email: toEmail || null },
        { withCredentials: true });
      setState('sent');
      setFeedback(`Sent to ${r.data.sent_to}${r.data.mocked ? ' · mocked' : ''}`);
    } catch (e) {
      setState('error');
      setFeedback(e?.response?.data?.detail || 'Send failed.');
    }
  };
  return (
    <div className="mt-2.5">
      <button onClick={onClick} disabled={state === 'sending' || state === 'sent'}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-[10px] font-mono uppercase tracking-[0.18em] border disabled:opacity-50"
        style={{ borderColor: state === 'sent' ? '#16624B' : (state === 'error' ? '#B0312D' : MUTED),
                 color: state === 'sent' ? '#16624B' : (state === 'error' ? '#B0312D' : SLATE),
                 backgroundColor: state === 'sent' ? 'rgba(22,98,75,0.06)' : 'transparent' }}
        data-testid={testid}>
        {state === 'sent' ? <><CheckCircle size={11} weight="fill" /> Sent</>
         : state === 'sending' ? 'Sending…'
         : state === 'error' ? <><Warning size={11} weight="bold" /> Retry</>
         : <><PaperPlaneTilt size={11} weight="bold" /> {label}</>}
      </button>
      {feedback && <p className="text-[10px] mt-1.5" style={{ color: state === 'error' ? '#B0312D' : MUTED }}
                       data-testid={`${testid}-feedback`}>{feedback}</p>}
    </div>
  );
}

function EmailPreview({ from, to, subject, body }) {
  return (
    <div className="rounded-sm border" style={{ borderColor: RULE, backgroundColor: IVORY }}>
      <div className="p-3 border-b" style={{ borderColor: RULE }}>
        <Row k="From" v={from} /><Row k="To" v={to} /><Row k="Subject" v={subject} />
      </div>
      <div className="p-5 text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: SLATE }}>{body}</div>
    </div>
  );
}

function RoutingStatusPanel() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/auth/me`, { withCredentials: true }).then(r => setMe(r.data)).catch(() => {});
  }, []);
  return (
    <section className="rounded-sm border p-6 mb-10" style={{ borderColor: HALE_RED, backgroundColor: 'rgba(230,57,70,0.04)' }}
             data-testid="toxintel-routing-status">
      <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: HALE_RED }}>Email routing · regulatory@tagc.us</p>
      <h2 className="text-2xl font-extrabold mt-1" style={{ fontFamily: '"Cormorant Garamond",serif', color: SLATE }}>
        Routing destination for all ToxIntel + Hale submissions
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mt-4">
        <div>
          <Row k="Address"          v={ROUTING_EMAIL} />
          <Row k="ToxIntel account" v={`${ROUTING_EMAIL} (role: ag_reviewer · seeded)`} />
          <Row k="App-side dispatcher" v="services/notifications.send_email · SendGrid client" />
          <Row k="Send config"      v="SENDGRID_API_KEY env var (status below)" />
          <Row k="Inbound mailbox"  v="External · not hosted by this app" />
        </div>
        <div>
          <Row k="Routed-to since" v="2026-02 (Hale workflow live)" />
          <Row k="Workflows routed" v="Hale intake · AG complete · clarification · ToxIntel samples" />
          <Row k="Notification audit" v="db.notifications (persisted regardless of SendGrid)" />
          <Row k="Logged-in admin"  v={me?.email || '—'} />
          <Row k="Current routing status" v={
            <span className="px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-[0.2em]"
                  style={{ backgroundColor: 'rgba(160,122,20,0.10)', color: '#A07A14', border: '1px solid #A07A14' }}>
              Send wired · inbound unverified
            </span>
          } />
        </div>
      </div>
      <div className="rounded-sm border p-4 mt-4 text-[12.5px] leading-relaxed" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)', color: SLATE }}>
        <p className="font-semibold mb-1">How to check this mailbox</p>
        <p style={{ color: MUTED }}>
          <code>regulatory@tagc.us</code> is an <b>external mailbox on the tagc.us domain</b> — its inbox is NOT hosted by this
          application. To verify it receives mail you need to (a) confirm an MX record on <code>tagc.us</code> points
          at a real mail provider (Google Workspace, Microsoft 365, Zoho, Fastmail, etc.) and (b) sign in to that provider
          with the account owner. This app cannot read external mailboxes. If you don&apos;t know which provider hosts
          tagc.us mail, check your domain registrar&apos;s DNS panel for the active MX record, or contact Emergent Support.
        </p>
        <p className="font-semibold mt-3 mb-1">If the mailbox is not yet configured</p>
        <ul className="list-disc ml-5" style={{ color: MUTED }}>
          <li>Recommended path: provision <code>regulatory@tagc.us</code> as a real mailbox or alias on the existing tagc.us mail provider — do <b>not</b> create a duplicate elsewhere.</li>
          <li>Required DNS records (set at your registrar): <code>MX</code>, <code>SPF</code> (<code>v=spf1 include:sendgrid.net include:&lt;provider&gt; ~all</code>), <code>DKIM</code> from your sending provider, and a <code>DMARC</code> policy record.</li>
          <li>Email passwords must <b>never</b> live in this codebase — store them in your mail provider&apos;s admin, not in <code>.env</code>.</li>
        </ul>
        <p className="font-semibold mt-3 mb-1">Outbound send status (this app)</p>
        <p style={{ color: MUTED }}>
          The app dispatches via <code>services/notifications.send_email</code>. Without a <code>SENDGRID_API_KEY</code>
          the helper runs in mocked mode (every send is still logged to <code>db.notifications</code> + per-application
          <code>notification_log</code>). With the key set, real mail dispatches. <b>Use the test button below to verify
          end-to-end delivery to <code>{ROUTING_EMAIL}</code>.</b>
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <SendSampleButton templateId="intake" toEmail={ROUTING_EMAIL}
                            label={`Send routing test to ${ROUTING_EMAIL}`}
                            testid="toxintel-routing-test-send" />
          <SendSampleButton templateId="intake" label="Send routing test to me" testid="toxintel-routing-test-self" />
        </div>
      </div>
    </section>
  );
}

function BadgeOfReleaseToxIntel() {
  return (
    <span className="inline-block px-2 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-[0.2em]"
          style={{ border: `1px solid ${CORAL}`, color: CORAL }}>
      ToxIntel · scientific authority
    </span>
  );
}

export default function ToxIntelSamplesPage() {
  const [filter, setFilter] = useState('all');
  const filtered = useMemo(() => {
    if (filter === 'all')      return SAMPLES;
    if (filter === 'client')   return SAMPLES.filter(s => /Brand|Client/.test(s.audience));
    if (filter === 'admin')    return SAMPLES.filter(s => /Admin/.test(s.audience));
    if (filter === 'internal') return SAMPLES.filter(s => /Internal|Reviewer|AG/.test(s.audience));
    return SAMPLES;
  }, [filter]);
  const print = () => window.print();
  return (
    <div className="min-h-screen" style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}
         data-testid="toxintel-samples-page">
      <Helmet><title>ToxIntel Workflow Samples · Generic Outputs</title></Helmet>

      <header className="border-b print:hidden" style={{ borderColor: RULE }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-end gap-3">
            <span className="text-xl tracking-tight" style={{ color: SLATE, fontFamily: '"Cormorant Garamond", serif' }}>
              <TI color={CORAL} />
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] px-2 py-1 rounded-sm"
                  style={{ backgroundColor: SLATE, color: IVORY }}>
              · Admin · ToxIntel · Workflow Samples
            </span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/admin/hale" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }}>← Hale admin hub</Link>
            <Link to="/admin/hale/samples" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }}>Hale samples →</Link>
            <button onClick={print} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-[11px] font-mono uppercase tracking-wider border"
                    style={{ borderColor: SLATE, color: SLATE, backgroundColor: IVORY }}>
              <Printer size={12} weight="bold" /> Export PDF
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 lg:py-14">
        <div className="mb-8">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>Workflow samples · generic outputs</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
              data-testid="toxintel-samples-title">
            Generic <TI color={CORAL} /> workflow output library.
          </h1>
          <p className="mt-3 text-sm max-w-3xl" style={{ color: MUTED }}>
            Reference samples for every stage of the <TI color={CORAL} /> assessment workflow, using only synthetic
            data ({SAMPLE.brand} · {SAMPLE.product}). Brand-facing outputs never leak internal reviewer notes —
            <TI color={CORAL} /> retains scientific authority; client or partner platform (e.g. Hale) retains final
            commercial/program decision authority where applicable.
          </p>
        </div>

        {/* Routing status — pinned to top per spec */}
        <RoutingStatusPanel />

        {/* Audience filter */}
        <div className="flex flex-wrap gap-2 mb-6 print:hidden" data-testid="toxintel-samples-filter">
          {[
            { id: 'all', label: 'All' },
            { id: 'client', label: 'Client / Brand-facing' },
            { id: 'admin', label: 'ToxIntel admin' },
            { id: 'internal', label: 'Reviewer / Internal' },
          ].map(t => (
            <button key={t.id} onClick={() => setFilter(t.id)}
                    className="px-3 py-1.5 rounded-sm text-[11px] font-mono uppercase tracking-wider border"
                    style={{ borderColor: filter === t.id ? CORAL : RULE,
                             color: filter === t.id ? CORAL : MUTED,
                             backgroundColor: filter === t.id ? 'rgba(255,78,106,0.06)' : 'transparent' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Index */}
        <section className="rounded-sm border mb-10" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }} data-testid="toxintel-samples-index">
          <div className="p-4 border-b" style={{ borderColor: RULE }}>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
              Index · {filtered.length} of {SAMPLES.length} samples
            </p>
          </div>
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left" style={{ color: MUTED }}>
                <th className="px-4 py-2 font-mono uppercase tracking-wider text-[10px]">#</th>
                <th className="px-4 py-2 font-mono uppercase tracking-wider text-[10px]">Output</th>
                <th className="px-4 py-2 font-mono uppercase tracking-wider text-[10px]">Audience</th>
                <th className="px-4 py-2 font-mono uppercase tracking-wider text-[10px]">Format</th>
                <th className="px-4 py-2 font-mono uppercase tracking-wider text-[10px]">Client-visible</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className="border-t" style={{ borderColor: RULE }}>
                  <td className="px-4 py-2.5 font-mono" style={{ color: MUTED }}>{s.id}</td>
                  <td className="px-4 py-2.5"><a href={`#toxintel-sample-${s.id}`} className="font-semibold hover:underline" style={{ color: SLATE }}>{s.title}</a></td>
                  <td className="px-4 py-2.5" style={{ color: MUTED }}>{s.audience}</td>
                  <td className="px-4 py-2.5" style={{ color: MUTED }}>{s.format}</td>
                  <td className="px-4 py-2.5" style={{ color: s.visible === true ? '#16624B' : s.visible === 'partial' ? '#A07A14' : '#B0312D' }}>
                    {s.visible === true ? 'Yes' : s.visible === 'partial' ? 'Some' : 'No'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* 01 */}
        <Section id="toxintel-sample-01" title="Assessment Request Confirmation" audience="Brand" format="Dashboard">
          <Row k="Brand" v={SAMPLE.brand} /><Row k="Product" v={SAMPLE.product} />
          <Row k="Assessment requested" v="Quantitative Risk Assessment (QRA) · leave-on" />
          <Row k="Documents uploaded" v="Formula · Packaging · Use instructions · Claims copy" />
          <Row k="Missing documents" v="SDS · COA · Lab test data" />
          <Row k="Status" v="Submitted" /><Row k="Routing ID" v={SAMPLE.routing_id} />
          <p className="text-[12px] mt-3" style={{ color: SLATE }}>
            <b>Next:</b> ToxIntel will review for completeness before scientific review begins.
          </p>
        </Section>

        {/* 02 */}
        <Section id="toxintel-sample-02" title="Admin Submission Alert" audience="AG · Internal" format="Email">
          <EmailPreview from="toxintel-notifications@toxintel.tagc.us" to={ROUTING_EMAIL}
            subject={`[ToxIntel] New assessment request — ${SAMPLE.brand} · ${SAMPLE.product}`}
            body={`A new ToxIntel assessment request has been routed.\n\nBrand:           ${SAMPLE.brand}\nProduct:         ${SAMPLE.product}\nCategory:        ${SAMPLE.category}\nAssessment type: QRA · leave-on\nDocs uploaded:   Formula, Packaging, Use instructions, Claims copy\nDocs missing:    SDS, COA, Lab test data\nRequested by:    2026-02-28 (placeholder)\nRouting ID:      ${SAMPLE.routing_id}\n\nOpen the reviewer queue:\nhttps://toxintel.tagc.us/assessor/hale-review\n\nToxIntel · scientific review partner: The Agyekum Group`} />
          <SendSampleButton templateId="intake" testid="toxintel-sample-02-send" />
        </Section>

        {/* 03 */}
        <Section id="toxintel-sample-03" title="ToxIntel Admin Intake Record" audience="ToxIntel Admin" format="Record">
          <SummaryCards items={[
            { key: 'category', label: 'Category',     value: 'Body care' },
            { key: 'type',     label: 'Assessment',   value: 'QRA' },
            { key: 'docs',     label: 'Docs',         value: '4/7' },
            { key: 'missing',  label: 'Missing',      value: 3, tone: '#A07A14' },
            { key: 'rev',      label: 'Reviewer',     value: 'Pending' },
          ]} testidPrefix="toxintel-sample-03-summary" />
          <Row k="Submission ID" v={SAMPLE.routing_id} /><Row k="Contact" v={SAMPLE.contact} />
          <Row k="Product" v={SAMPLE.product} /><Row k="Services requested" v="QRA · MoCRA readiness · Claims substantiation" />
          <Row k="Assigned reviewer" v="Awaiting pickup" />
          <Row k="Internal notes" v="Standard leave-on intake; expect supplier doc follow-up." />
        </Section>

        {/* 04 */}
        <Section id="toxintel-sample-04" title="Document Checklist Output" audience="ToxIntel Admin · Reviewer" format="Record">
          <DocumentChecklist value={CHECKLIST} onChange={() => {}} disabled />
          <p className="text-[11px] mt-2 italic" style={{ color: MUTED }}>
            Status options: Received · Missing · Not Applicable · Needs Clarification (use the Missing state with a reviewer note for the last).
          </p>
        </Section>

        {/* 05 */}
        <Section id="toxintel-sample-05" title="Reviewer Intake Summary" audience="AG · Reviewer" format="Record">
          <Row k="Brand overview" v={`${SAMPLE.brand} · founded 2024`} />
          <Row k="Product overview" v={`${SAMPLE.product} · leave-on body care`} />
          <Row k="Category" v={SAMPLE.category} /><Row k="Route of exposure" v="Dermal · daily, full-body" />
          <Row k="Assessment type" v="QRA · MoCRA readiness" />
          <Row k="Documents reviewed" v="Formula · Packaging · Use instructions · Claims copy" />
          <Row k="Missing documents" v="SDS · COA · Lab test data" />
          <Row k="Initial risk flags" v="Fragrance compound 2A · daily-use dermal exposure" />
          <Row k="Reviewer assignment" v={ROUTING_EMAIL} /><Row k="Review status" v="Intake → Document review (pending)" />
        </Section>

        {/* 06 */}
        <Section id="toxintel-sample-06" title="Additional Information Request" audience="Brand" format="Email">
          <EmailPreview from="toxintel-notifications@toxintel.tagc.us" to={SAMPLE.contact}
            subject={`ToxIntel status update — Additional Information Required`}
            body={`Hello,\n\nYour ${SAMPLE.product} assessment is in scientific review. Three items are outstanding:\n\n  1. SDS for core formula\n  2. COA for active ingredient batch\n  3. Lab test data referenced in your claims dossier\n\nDeadline (placeholder): 2026-02-28.\nStatus: Additional Information Required\n\nInternal reviewer note (not shown to brand in production): Confirm fragrance compound 2A supplier docs alongside the above.`} />
          <SendSampleButton templateId="documents_needed" testid="toxintel-sample-06-send" />
        </Section>

        {/* 07 */}
        <Section id="toxintel-sample-07" title="TRA / QRA Assessment Sample" audience="Internal + Released" format="Record">
          <Row k="Scope" v="Quantitative Risk Assessment · leave-on dermal exposure" />
          <Row k="Product category" v={SAMPLE.category} /><Row k="Exposure assumptions" v="Daily, full body, 10 g/day applied · placeholder" />
          <Row k="Ingredients reviewed" v="14 INCI entries · placeholder" />
          <Row k="Risk considerations" v="Fragrance compound 2A · skin sensitisation pathway" />
          <Row k="Margin of safety" v="MoS = 320 (placeholder · target ≥ 100)" />
          <Row k="Findings" v="No safety concerns at modelled exposure · 1 conditional item." />
          <Row k="Recommendation" v="Recommended with Conditions" />
          <p className="text-[10px] italic mt-3" style={{ color: MUTED }}>
            Disclaimer: SAMPLE output. Not a legally final assessment. Generic placeholder values only.
          </p>
        </Section>

        {/* 08 */}
        <Section id="toxintel-sample-08" title="Prop 65 Exposure Assessment Sample" audience="Internal + Released" format="Record">
          <Row k="Product" v={SAMPLE.product} />
          <Row k="Chemical of concern" v="Placeholder substance X (CAS XXXX-XX-X)" />
          <Row k="Exposure pathway" v="Dermal · daily leave-on · placeholder" />
          <Row k="Assumptions" v="Bodyweight 60kg, 10g/day · placeholder" />
          <Row k="Screening calculation" v="Daily dose 0.02 µg/kg-day · placeholder" />
          <Row k="Safe harbor comparison" v="Below NSRL placeholder (0.5 µg/day)" />
          <Row k="Risk conclusion" v="No Prop 65 warning required at modelled exposure · placeholder" />
          <p className="text-[10px] italic mt-3" style={{ color: MUTED }}>SAMPLE — Generic ToxIntel Output. Not a real Prop 65 assessment.</p>
        </Section>

        {/* 09 */}
        <Section id="toxintel-sample-09" title="Regulatory Readiness Review Sample" audience="Brand" format="Record">
          <Row k="MoCRA readiness" v="Substantially compliant · 1 SOP gap flagged" />
          <Row k="Prop 65 readiness" v="No warning required at modelled exposure" />
          <Row k="SDS / documentation" v="Partial · SDS missing" />
          <Row k="Retailer documentation" v="Sephora docs satisfied · Ulta docs satisfied" />
          <Row k="Missing" v="SDS · COA · adverse event reporting SOP" />
          <Row k="Recommended next steps" v="Upload SDS+COA · adopt AE reporting SOP · re-screen in 12 months" />
        </Section>

        {/* 10 */}
        <Section id="toxintel-sample-10" title="Claims Substantiation Review Sample" audience="Brand" format="Record">
          <Row k="Claim reviewed" v="&quot;Clinically proven to hydrate 24 hours&quot;" />
          <Row k="Evidence provided" v="Internal panel n=20, no published study" />
          <Row k="Risk level" v="Moderate" />
          <Row k="Suggested claim language" v="&quot;Provides long-lasting hydration in user trials (n=20)&quot;" />
          <Row k="Discouraged claim language" v="&quot;Clinically proven&quot;, &quot;dermatologist-tested&quot; without referenced study" />
          <Row k="Required support" v="Third-party clinical study or revised wording" />
          <Row k="Notes" v="Re-test claim post-substantiation upgrade." />
        </Section>

        {/* 11 */}
        <Section id="toxintel-sample-11" title="Packaging / RSL Intelligence Sample" audience="Brand · Internal" format="Record">
          <Row k="Packaging material" v="PCR PET · 50% recycled content" />
          <Row k="Potential restricted substances" v="Phthalates · BPA · heavy metal residues" />
          <Row k="Supplier documentation needed" v="RSL declaration · migration test report" />
          <Row k="Screening status" v="Pending supplier docs" />
          <Row k="Testing recommendation" v="Migration testing per EU 10/2011 · placeholder" />
          <Row k="Retailer notes" v="Sephora packaging requirements satisfied pending RSL doc." />
        </Section>

        {/* 12 */}
        <Section id="toxintel-sample-12" title="Scientific Output Complete" audience="ToxIntel Admin · Brand · Hale" format="Record">
          <Row k="Review summary" v="Assessment complete; 1 conditional finding; 0 blocking issues." />
          <Row k="Conditions" v="Confirm supplier RSL declaration within 14 days of activation." />
          <Row k="Recommendation" v="Recommended with Conditions" />
          <p className="text-[11px] mt-3 italic" style={{ color: MUTED }}>
            <TI color={CORAL} /> / AG controls scientific audit output. Client or partner platform (e.g. Hale) controls final commercial/program decision where applicable.
          </p>
          <SendSampleButton templateId="ag_complete" testid="toxintel-sample-12-send" />
        </Section>

        {/* 13 — internal only, no brand send */}
        <Section id="toxintel-sample-13" title="Internal Reviewer Notes Sample" audience="Internal Only" format="Record">
          <div className="rounded-sm border p-3" style={{ borderColor: RULE, backgroundColor: 'rgba(11,20,38,0.04)' }}>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: HALE_RED }}>NEVER shown to brand</p>
            <p className="text-[12.5px] mt-1" style={{ color: SLATE }}>
              Observations: vendor responsiveness on supplier docs has been slow; recommend conditional approval pending docs within 14 days. Scientific rationale: MoS &gt; 300 for the worst-case
              fragrance compound; risk is acceptable subject to the supplier confirmation. Unreleased concern: ingredient sourcing region recently flagged for adulteration in
              public RSL data — recommend additional COA on next batch.
            </p>
            <p className="text-[12.5px] mt-2" style={{ color: SLATE }}>Follow-up: re-test post-supplier confirmation; do not release reviewer identity to brand.</p>
          </div>
        </Section>

        {/* 14 */}
        <Section id="toxintel-sample-14" title="Released Findings Sample" audience="Brand · Hale" format="Record">
          <p className="text-[13px]" style={{ color: SLATE }}>
            <b>Plain-language summary:</b> {SAMPLE.brand}&apos;s {SAMPLE.product} has been scientifically reviewed for leave-on dermal exposure. No safety concerns were identified at the
            modelled exposure level. One conditional item is outstanding: supplier documentation for one fragrance compound.
          </p>
          <ul className="list-disc ml-5 text-[13px] mt-3" style={{ color: SLATE }}>
            <li><b>Key findings:</b> margin of safety satisfies standard benchmarks · packaging within retailer requirements pending supplier RSL declaration.</li>
            <li><b>Conditions:</b> supplier documentation for fragrance compound 2A within 14 days of activation.</li>
            <li><b>Recommended next steps:</b> upload supplier documentation; we&apos;ll re-screen and release the unconditional output.</li>
          </ul>
          <p className="text-[10px] italic mt-3" style={{ color: MUTED }}>No internal reviewer notes included. Approved/released language only.</p>
        </Section>

        {/* 15 */}
        <Section id="toxintel-sample-15" title="ToxIntel Dashboard Status Update" audience="Brand" format="Dashboard">
          <ul className="text-[12.5px] grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5" style={{ color: SLATE }}>
            {['Submitted','Intake Review','Documents Needed','Ready for Scientific Review','Scientific Review in Progress','Reviewer Questions','Output Complete','Released to Client','Closed']
              .map((label, i) => (
                <li key={label} className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: i === 7 ? CORAL : (i < 7 ? SLATE : 'transparent'), border: `1px solid ${i < 8 ? SLATE : MUTED}` }} />
                  <span style={{ fontWeight: i === 7 ? 700 : 400 }}>{label}</span>
                </li>
              ))}
          </ul>
        </Section>

        {/* 16 */}
        <Section id="toxintel-sample-16" title="Reviewer Queue Timeline" audience="ToxIntel Admin · Reviewer" format="Timeline">
          <AuditTimeline
            stages={['Submission received','Intake reviewed','Documents requested','Documents received','Scientific review started','Draft output created','Internal review complete','Released findings approved','Output sent','Closed']}
            currentIndex={9} testid="toxintel-sample-16-timeline" />
        </Section>

        {/* 17 — email library */}
        <Section id="toxintel-sample-17" title="Email Template Library" audience="Mixed" format="Email">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ['intake',           'Submission received',        'AG · Internal', 'Routed to regulatory@tagc.us.'],
              ['documents_needed', 'Documents needed',           'Brand',         'Outstanding items — please upload.'],
              ['review_started',   'Review started',             'Brand',         'Scientific review has begun.'],
              ['reviewer_question','Reviewer question',          'Brand',         'A reviewer needs clarification.'],
              ['ag_complete',      'Draft output ready',         'Hale',          'AG draft complete — pending internal sign-off.'],
              ['ag_complete',      'Scientific output complete', 'Hale',          'AG audit complete — decision pending.'],
              ['hale_approved',    'Released findings sent',     'Brand',         'Findings released to client.'],
              ['badge_issued',     'Assessment closed',          'Brand',         'Assessment closed — final findings delivered.'],
              ['intake',           `Admin alert to ${ROUTING_EMAIL}`, 'AG · Internal', 'Routing target — every submission lands here.'],
            ].map(([id, title, audience, body], idx) => (
              <div key={`${id}-${idx}`} className="rounded-sm border p-3" style={{ borderColor: RULE, backgroundColor: IVORY }}>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: CORAL }}>{audience}</p>
                <p className="text-[13px] font-semibold mt-1" style={{ color: SLATE }}>{title}</p>
                <p className="text-[12px] mt-1" style={{ color: MUTED }}>{body}</p>
                <SendSampleButton templateId={id} testid={`toxintel-sample-17-send-${idx}`} />
              </div>
            ))}
          </div>
        </Section>

        {/* 18 — PDF */}
        <Section id="toxintel-sample-18" title="PDF / Report Sample" audience="ToxIntel Admin" format="PDF">
          <p className="text-[11px] mb-3" style={{ color: MUTED }}>Print or save-as-PDF via the top-bar Export button.</p>
          <div className="rounded-sm border p-7" style={{ borderColor: RULE, backgroundColor: 'white' }}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: HALE_RED }}>SAMPLE — Generic ToxIntel Output · Not a real assessment</p>
              <BadgeOfReleaseToxIntel />
            </div>
            <h4 className="text-2xl font-extrabold mt-2" style={{ fontFamily: '"Cormorant Garamond",serif', color: SLATE }}>
              <TI color={CORAL} /> assessment · {SAMPLE.brand} · {SAMPLE.product}
            </h4>
            <p className="text-[12px] mt-1" style={{ color: MUTED }}>Cover · Scope · Documents reviewed · Exposure assumptions · Findings · Recommendation · Conditions · Audit trail · Disclaimer.</p>
            <div className="mt-4">
              <Row k="Scope"                v="QRA · leave-on dermal exposure" />
              <Row k="Documents reviewed"   v="Formula, Packaging, Use instructions, Claims copy" />
              <Row k="Exposure assumptions" v="60 kg bodyweight · 10 g/day · daily leave-on · placeholder" />
              <Row k="Findings"             v="No safety concerns at modelled exposure; 1 conditional finding." />
              <Row k="Recommendation"       v="Recommended with Conditions" />
              <Row k="Conditions"           v="Supplier documentation for fragrance compound 2A within 14 days." />
              <Row k="Audit trail"          v="Submitted → Intake → Docs requested → Review → Internal QA → Released → Closed" />
            </div>
            <p className="text-[10px] italic mt-4" style={{ color: MUTED }}>
              Disclaimer: SAMPLE output. Not legally final. <TI color={CORAL} /> / AG retains scientific authority; client or partner platform retains final commercial/program decision.
            </p>
          </div>
        </Section>

        {/* 19 — export inventory */}
        <Section id="toxintel-sample-19" title="Export Inventory" audience="ToxIntel Admin" format="Record">
          <ul className="text-[12.5px]" style={{ color: SLATE }}>
            {SAMPLES.map(s => (
              <li key={s.id} className="py-1 border-b" style={{ borderColor: RULE }}>
                <span className="font-mono" style={{ color: MUTED }}>{s.id}</span> · <b>{s.title}</b> · <span style={{ color: MUTED }}>{s.audience} · {s.format} · client-visible: {s.visible === true ? 'Yes' : s.visible === 'partial' ? 'Some' : 'No'}</span>
              </li>
            ))}
          </ul>
        </Section>

        <p className="text-[11px] mt-10 text-center" style={{ color: MUTED }}>
          All samples on this page use synthetic data only. No real client, brand, formula, lab, or confidential data is referenced.
        </p>
      </main>
    </div>
  );
}
